/**
 * The facts the Terms of Service and Privacy Policy state about the operator
 * (SOL-197), in one place so the two documents, the footer and Stripe's
 * checkout consent cannot disagree.
 *
 * A `null` is a fact nobody has confirmed yet. The pages render it as a
 * visible placeholder and show a draft banner while any remains, so an
 * unreviewed document can never pass for a final one. Fill these in only from
 * the company's records or the lawyer's review — never from a guess.
 */
export interface LegalFacts {
  /** The legal entity that runs Memoar and is the data controller. */
  operator: string
  operatorDescription: string
  /** Registered address of the operator. */
  address: string | null
  /** Where customers write for support, refunds and privacy requests (SOL-200). */
  contactEmail: string | null
  /**
   * GDPR Art. 27: a controller outside the EU that offers services to people in
   * the EU must name a representative inside it. Solexma is a US LLC.
   */
  euRepresentative: string | null
  /** The law and courts the Terms are governed by, as the lawyer words it. */
  governingLaw: string | null
  /** The date the reviewed documents take effect. */
  effectiveDate: string | null
}

/** Where customers write (SOL-200). The worker's `SUPPORT_EMAIL` is the same address. */
export const SUPPORT_EMAIL = 'support@memoar.me'

export const LEGAL: LegalFacts = {
  operator: 'Solexma LLC',
  operatorDescription: 'a limited liability company registered in Wyoming, USA',
  address: '30 N Gould St # 32721, Sheridan, WY 82801, USA',
  contactEmail: SUPPORT_EMAIL,
  euRepresentative: 'DataRep',
  governingLaw: 'the laws of the State of Wyoming, USA',
  effectiveDate: null,
}

/** True while any fact above is unconfirmed: the documents are drafts. */
export function legalIsDraft(facts: LegalFacts = LEGAL): boolean {
  return Object.values(facts).some((v) => v === null)
}

/**
 * Where the documents live. Absolute, because Stripe's checkout links to the
 * Terms from its own domain, and the landing links here from memoar.me.
 */
export const APP_ORIGIN = 'https://app.memoar.me'
export const TERMS_PATH = '/terms'
export const PRIVACY_PATH = '/privacy'
